import React, { useEffect, useMemo } from "react";
import * as Scrivito from "scrivito";
import {
  useProductActions,
  useProductOutput,
  useActiveTab,
  useSetActiveTab,
  ConfiguratorTabs,
  resetProductUIStore,
} from "stores/product";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { OrderCodeForm } from "./components/OrderCodeForm/OrderCodeForm";
import { ProductDownloads } from "./components/Downloads/ProductDownloads";
import { TechnicalProperties } from "./components/Results/TechnicalProperties";
import { ProductConfiguratorLayout } from "./ProductConfiguratorLayout";
import { TechnicalPropertiesSimpleView } from "./components/Results/TechnicalPropertiesSimpleView";
import { findKeylistBySeriesId, getCategoryOrSeriesMap } from "utils";
import { useProductsOverviewActions } from "stores/productsOverviewStore";

export const ProductConfigurator = Scrivito.connect(() => {
  const productActions = useProductActions();
  const activeTab = useActiveTab();
  const productOutput = useProductOutput();
  const setActiveTab = useSetActiveTab();
  const { setProductCategoryId } = useProductsOverviewActions();

  useEffect(
    () => setActiveTab(ConfiguratorTabs.TECHNICAL_DESIGN),
    [setActiveTab],
  );
  const { productId: productIdParam, seriesId: seriesIdParam } =
    Scrivito.currentPageParams();
  const allProducts = useAllProducts();

  const [, seriesMap] = useMemo(
    () =>
      getCategoryOrSeriesMap(
        allProducts as Parameters<typeof getCategoryOrSeriesMap>[0],
      ),
    [allProducts],
  );
  const keyList = useMemo(
    () =>
      seriesIdParam
        ? findKeylistBySeriesId({
            seriesMap,
            productSeriesId: seriesIdParam as string,
          })
        : [],
    [seriesMap, seriesIdParam],
  );

  useEffect(() => {
    setProductCategoryId(keyList);
  }, [keyList, setProductCategoryId]);

  useEffect(() => {
    const seriesId =
      seriesIdParam &&
      typeof seriesIdParam === "string" &&
      !isNaN(Number(seriesIdParam))
        ? Number(seriesIdParam)
        : null;
    const productId =
      productIdParam && typeof productIdParam === "string"
        ? productIdParam
        : null;
    if (productId && seriesId) {
      productActions.loadNode({
        nodeId: productId,
        seriesId: seriesId,
      });
    } else if (seriesId) {
      productActions.loadProduct({
        seriesId: seriesId,
      });
    }

    return () => {
      productActions.clear();
      resetProductUIStore();
    };
  }, [productActions, productIdParam, seriesIdParam]);

  const hasInputs =
    !!productOutput?.product?.inputs && productOutput.product.inputs.length > 0;

  return (
    <ProductConfiguratorLayout>
      {activeTab === ConfiguratorTabs.DOWNLOADS && <ProductDownloads />}
      {activeTab === ConfiguratorTabs.TECHNICAL_DESIGN &&
        (hasInputs ? (
          <TechnicalProperties />
        ) : (
          <TechnicalPropertiesSimpleView />
        ))}
      {activeTab === ConfiguratorTabs.PRODUCT_SPECIFICATION && (
        <OrderCodeForm />
      )}
    </ProductConfiguratorLayout>
  );
});
